import { SensorType } from "../../models/sensors/Sensor"
import { MissionUtils } from "../../utils/MissionUtils"
import { initialSelectedMission, initialState } from "../ContextState"
import { MissionActiveFlow, MissionType, MissionsActions, MissionsType } from "../types/MissionsTypes"
import { Types } from "../types/Types"

// fields that should not equal each other
const complements: { [key: string]: string } = {
    sensor_id_1: "sensor_id_2",
    sensor_id_2: "sensor_id_1",
    operator_1_id: "operator_2_id",
    operator_2_id: "operator_1_id",
    pilot_1_id: "pilot_2_id",
    pilot_2_id: "pilot_1_id",
}

export const missionsReducer = (state: MissionsType, action: MissionsActions): MissionsType => {
    switch (action.type) {
        case Types.Missions_ResetState: {
            return initialState.missions
        }
        case Types.Missions_SelectParsedMission: {
            const { selectedMission: newSelectedMission, sensors } = action.payload
            const { serial_number_1, serial_number_2 } = newSelectedMission

            const findSensorBySerial = (serial: string) =>
                sensors.find((s: SensorType) => s.serial_number === serial) ?? null

            const sensor1 = findSensorBySerial(serial_number_1)
            let sensor2: SensorType | null = null
            if (serial_number_2 && serial_number_1 !== serial_number_2) {
                sensor2 = findSensorBySerial(serial_number_2)
            }

            // keep the user's edits if possible
            const mission: MissionType = {
                ...initialSelectedMission,
                ...state.selectedMission,
                // overwrite relevant fields with the new selection or keep the current info if undefined
                flown_date: newSelectedMission.flown_date ?? state.selectedMission.flown_date,
                source_file: newSelectedMission.source_file ?? state.selectedMission.source_file,
                flight_notes: newSelectedMission.flight_notes ?? state.selectedMission.flight_notes,
                sensor_notes: newSelectedMission.sensor_notes ?? state.selectedMission.sensor_notes,
                sensor_id_1: sensor1?.sensor_id ?? null,
                sensor_id_2: sensor2?.sensor_id ?? null,
                mission_name: MissionUtils.getMissionName(
                    sensor1?.serial_number,
                    newSelectedMission.flown_date,
                    newSelectedMission.flight_num,
                ),
            }

            return {
                ...state,
                selectedMission: mission,
                missionsErrorMessage: undefined,
            }
        }
        case Types.Missions_SelectMissionId: {
            return {
                ...state,
                selectedMission: initialSelectedMission,
                selectedMissionId: action.payload.selectedMissionId,
            }
        }
        case Types.Missions_SelectActiveFlow: {
            return { ...state, activeFlow: action.payload.activeFlow }
        }
        case Types.Missions_UpdateMissionData: {
            const { missionPart, sensors } = action.payload
            const { selectedMission, activeFlow } = state
            let newState: MissionType = { ...selectedMission }

            for (const field in missionPart) {
                const value = missionPart[field as keyof MissionType]
                const complement = complements[field]
                const complementValue = selectedMission[complement as keyof MissionType]

                newState = { ...newState, [field]: value }

                if (value && value === complementValue) {
                    newState = { ...newState, [complement]: null }
                }
            }

            // mission_name
            const { sensor_id_1, flown_date, flight_num } = newState

            const sensor = sensors.find((s) => s.sensor_id === sensor_id_1)
            const flightNum = activeFlow !== MissionActiveFlow.NO_FLIGHT ? flight_num : undefined

            return {
                ...state,
                selectedMission: {
                    ...newState,
                    mission_name: MissionUtils.getMissionName(sensor?.serial_number, flown_date, flightNum),
                },
            }
        }
        case Types.Missions_UpdateIsVendorFlow: {
            return {
                ...state,
                isVendorFlow: action.payload,
            }
        }
        case Types.Missions_FetchMission: {
            return { ...state, selectedMission: initialSelectedMission }
        }
        case Types.Missions_FetchMissionSuccess: {
            const { selectedMission } = action.payload

            let sorties = initialSelectedMission.sorties
            if (selectedMission.sorties) {
                sorties = selectedMission.sorties.map((sortie, index) => ({ ...sortie, frontendId: index + 1 }))
            }

            return {
                ...state,
                selectedMission: { ...initialSelectedMission, ...selectedMission, sorties },
                isVendorFlow: Boolean(selectedMission.vendor_name),
            }
        }
        case Types.Missions_DeleteMission: {
            return {
                ...state,
                isDeleting: true,
            }
        }
        case Types.Missions_DeleteMissionSuccess:
        case Types.Missions_DeleteMissionError: {
            return {
                ...state,
                isDeleting: false,
            }
        }
        case Types.Missions_FetchParsedMissions: {
            return { ...state, parsedMissions: [] }
        }
        case Types.Missions_FetchParsedMissionsSuccess: {
            const { parsedMissions, warnings } = action.payload

            return {
                ...state,
                parsedMissions,
                warnings,
            }
        }
        case Types.Missions_FetchEmployeeList: {
            return {
                ...state,
                employeeList: initialState.missions.employeeList,
            }
        }
        case Types.Missions_FetchEmployeeListSuccess: {
            return {
                ...state,
                employeeList: action.payload.employeeList,
            }
        }
        case Types.Missions_FetchVendorNameList: {
            return {
                ...state,
                vendorNameList: initialState.missions.vendorNameList,
            }
        }
        case Types.Missions_FetchVendorNameListSuccess: {
            return {
                ...state,
                vendorNameList: action.payload.vendorNameList,
            }
        }
        case Types.Missions_FetchPlatformList: {
            return {
                ...state,
                platformList: { platforms: [], project_platforms: [] },
            }
        }
        case Types.Missions_FetchPlatformListSuccess: {
            return {
                ...state,
                platformList: action.payload,
            }
        }
        case Types.Missions_FetchAirportList: {
            return {
                ...state,
                airportList: {
                    airports: [],
                    project_airports: [],
                },
            }
        }
        case Types.Missions_FetchAirportListSuccess: {
            return {
                ...state,
                airportList: action.payload,
            }
        }
        case Types.Missions_UpdateError: {
            const { error } = action.payload

            return {
                ...state,
                missionsErrorMessage: error,
            }
        }
        case Types.Missions_ClearError: {
            return { ...state, missionsErrorMessage: undefined }
        }
        default:
            return state
    }
}
